import React from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Tab,
  Tabs,
  TextField,
  MenuItem,
  Typography,
  Button,
  Breadcrumbs,
  CircularProgress,
} from '@mui/material';
 
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { app } from '@ink-ai/portal/reducers/app';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import LanguageIcon from '@mui/icons-material/Language';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BookOpenText } from 'lucide-react';
import { PaperCard } from '../components/Common';
import { DateTime } from 'luxon';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';

type RangeKey = 'yesterday' | 'week' | 'month' | 'custom';

interface Metric {
  label: string;
  value: number | string;
}

interface RankItem {
  name: string;
  value: number;
}

interface DashboardData {
  metrics: Metric[];
  xTicks: string[]; // labels for x-axis
  valuesA: number[];
  valuesB: number[];
  topUsers: RankItem[];
  topDepartments: RankItem[];
  usageBar: number[];
  usageLine: number[];
  docTypes: { name: string; value: number }[];
  successRate: { name: string; value: number }[];
  memberByChars: RankItem[];
  memberByDocs: RankItem[];
  externalMemberByDocs: RankItem[];
  teamByDocs: RankItem[];
  overviewDynamic: Metric[];
  overviewStatic: Metric[];
  memberByCharsCn: RankItem[];
  memberByCharsEn: RankItem[];
}

// --- Mock data generator ----------------------------------------------------
function generateMockData(range: RangeKey, baseDate: DateTime, t: (key: string, opts?: any) => string): DashboardData {
  const tTicks: string[] = [];
  const hours = [4, 8, 12, 16, 20, 24];
  hours.forEach((h) => tTicks.push(`${h}:00`));

  // Deterministic random based on date + range
  const seed = baseDate.toFormat('yyyyLLdd') + range;
  const rng = mulberry32(hashString(seed));

  const valuesA = Array.from({ length: hours.length }, () => 80 + Math.floor(rng() * 120));
  const valuesB = Array.from({ length: hours.length }, () => 70 + Math.floor(rng() * 110));

  const usageBar = Array.from({ length: hours.length }, () => 90 + Math.floor(rng() * 140));
  const usageLine = Array.from({ length: hours.length }, () => 70 + Math.floor(rng() * 160));

  const userCount = 15 + Math.floor(rng() * 12);
  const deptCount = 7 + Math.floor(rng() * 4);
  const totalDocs = 150 + Math.floor(rng() * 120);
  const allChars = 2000 + Math.floor(rng() * 4000);
  const cnVolume = 100000 + Math.floor(rng() * 90000);
  const enVolume = 80000 + Math.floor(rng() * 70000);
  const termLibs = 3 + Math.floor(rng() * 5);
  const caseLibs = 2 + Math.floor(rng() * 4);
  const termEntries = 5000 + Math.floor(rng() * 4000);
  const caseEntries = 2000 + Math.floor(rng() * 3000);

  const metrics: Metric[] = [
    { label: t('dashboard.metrics.userCounts', { defaultValue: '翻译用户数' }), value: userCount },
    { label: t('dashboard.metrics.departments', { defaultValue: 'Departments' }), value: deptCount },
    { label: t('dashboard.metrics.totalDocuments', { defaultValue: '翻译文档总数' }), value: totalDocs },
    { label: t('dashboard.metrics.allCharacters', { defaultValue: 'All characters' }), value: `${allChars}` },
    { label: t('dashboard.metrics.cnVolume', { defaultValue: '中文翻译量' }), value: cnVolume },
    { label: t('dashboard.metrics.enVolume', { defaultValue: '英文翻译量' }), value: enVolume },
    { label: t('dashboard.metrics.termLibs', { defaultValue: '术语文件总数' }), value: termLibs },
    { label: t('dashboard.metrics.caseLibs', { defaultValue: '样例文件总数' }), value: caseLibs },
    { label: t('dashboard.metrics.termEntries', { defaultValue: '术语词条总数' }), value: termEntries },
    { label: t('dashboard.metrics.caseEntries', { defaultValue: '样例词条数' }), value: caseEntries },
  ];

  const overviewDynamic: Metric[] = [
    { label: t('dashboard.metrics.userCounts', { defaultValue: '翻译用户数' }), value: userCount },
    { label: t('dashboard.metrics.totalDocuments', { defaultValue: '翻译文档总数' }), value: totalDocs },
    { label: t('dashboard.metrics.cnVolume', { defaultValue: '中文翻译量（万）' }), value: cnVolume },
    { label: t('dashboard.metrics.enVolume', { defaultValue: '英文翻译量（万）' }), value: enVolume },
  ];

  const overviewStatic: Metric[] = [
    { label: t('dashboard.metrics.termLibs', { defaultValue: '术语文件总数' }), value: termLibs },
    { label: t('dashboard.metrics.termEntries', { defaultValue: '术语词条总数' }), value: termEntries },
    { label: t('dashboard.metrics.caseLibs', { defaultValue: '样例文件总数' }), value: caseLibs },
    { label: t('dashboard.metrics.caseEntries', { defaultValue: '样例词条数' }), value: caseEntries },
  ];

  const userNames = ['User1','User2','User3','User4','User5'];
  const deptNames = ['Dept1','Dept2','Dept3','Dept4','Dept5'];
  const topUsers: RankItem[] = userNames.map((n) => ({ name: n, value: 100 + Math.floor(rng() * 220) }))
    .sort((a,b) => b.value - a.value);
  const topDepartments: RankItem[] = deptNames.map((n) => ({ name: n, value: 80 + Math.floor(rng() * 200) }))
    .sort((a,b) => b.value - a.value);

  const docTypes = [
    { name: 'DOCX', value: 40 + Math.floor(rng() * 60) },
    { name: 'PDF', value: 30 + Math.floor(rng() * 60) },
    { name: 'TXT', value: 10 + Math.floor(rng() * 30) },
    { name: 'Image', value: 15 + Math.floor(rng() * 35) },
  ];
  const success = 70 + Math.floor(rng() * 25);
  const fail = 100 - success;
  const successRate = [
    { name: t('dashboard.success', { defaultValue: '成功' }), value: success },
    { name: t('dashboard.fail', { defaultValue: '失败' }), value: fail },
  ];
  const memberByChars: RankItem[] = userNames.map((n) => ({ name: n, value: 100000 + Math.floor(rng() * 250000) }))
    .sort((a,b) => b.value - a.value);
  const memberByCharsCn: RankItem[] = userNames.map((n) => ({ name: n, value: 120000 + Math.floor(rng() * 250000) }))
    .sort((a,b) => b.value - a.value);
  const memberByCharsEn: RankItem[] = userNames.map((n) => ({ name: n, value: 80000 + Math.floor(rng() * 200000) }))
    .sort((a,b) => b.value - a.value);
  const memberByDocs: RankItem[] = userNames.map((n) => ({ name: n, value: 30 + Math.floor(rng() * 50) }))
    .sort((a,b) => b.value - a.value);
  const teamByDocs: RankItem[] = deptNames.map((n) => ({ name: n, value: 30 + Math.floor(rng() * 50) }))
    .sort((a,b) => b.value - a.value);
  const externalMemberByDocs: RankItem[] = userNames.map((n) => ({ name: `${n}@corp.com`, value: 20 + Math.floor(rng() * 40) }))
    .sort((a,b) => b.value - a.value);

  return {
    metrics,
    xTicks: tTicks,
    valuesA,
    valuesB,
    topUsers,
    topDepartments,
    usageBar,
    usageLine,
    docTypes,
    successRate,
    memberByChars,
    memberByDocs,
    teamByDocs,
    externalMemberByDocs,
    overviewDynamic,
    overviewStatic,
    memberByCharsCn,
    memberByCharsEn,
  };
}

function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(a: number) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- ECharts Line Chart -----------------------------------------------------
import * as echarts from 'echarts';
const LineChart: React.FC<{ data: DashboardData; single?: boolean; seriesName?: string }> = ({ data, single = false, seriesName }) => {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<echarts.EChartsType | null>(null);

  React.useEffect(() => {
    if (!ref.current) return;
    chartRef.current?.dispose();
    chartRef.current = echarts.init(ref.current);

    const option: echarts.EChartsCoreOption = {
      grid: { left: 40, right: 16, top: 20, bottom: 40 },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: data.xTicks },
      yAxis: { type: 'value' },
      series: single
        ? [
            {
              name: seriesName || t('dashboard.cards.totalDocs', { defaultValue: '文档总数' }),
              type: 'line',
              smooth: true,
              symbolSize: 6,
              itemStyle: { color: '#5B8FF9' },
              lineStyle: { width: 2 },
              data: data.valuesA,
            },
          ]
        : [
            {
              name: t('dashboard.series.a'),
              type: 'line',
              smooth: true,
              symbolSize: 6,
              itemStyle: { color: '#5B8FF9' },
              lineStyle: { width: 2 },
              data: data.valuesA,
            },
            {
              name: t('dashboard.series.b'),
              type: 'line',
              smooth: true,
              symbolSize: 6,
              itemStyle: { color: '#5AD8A6' },
              lineStyle: { width: 2 },
              data: data.valuesB,
            },
          ],
    };

    chartRef.current.setOption(option);

    const ro = new ResizeObserver(() => {
      chartRef.current?.resize();
    });
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [data, t, single, seriesName]);

  return <div ref={ref} style={{ width: '100%', height: 260 }} />;
};

// --- ECharts Ranking (Horizontal Bar) --------------------------------------
const RankingChart: React.FC<{ items: RankItem[] }> = ({ items }) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<echarts.EChartsType | null>(null);

  React.useEffect(() => {
    if (!ref.current) return;
    chartRef.current?.dispose();
    chartRef.current = echarts.init(ref.current);

    const names = items.map(i => i.name).reverse();
    const values = items.map(i => i.value).reverse();

    const option: echarts.EChartsCoreOption = {
      grid: { left: 80, right: 20, top: 20, bottom: 30 },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: names },
      series: [{
        type: 'bar',
        data: values,
        itemStyle: { color: '#5B8FF9' },
        barWidth: 18,
      }],
    };

    chartRef.current.setOption(option);
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(ref.current);
    return () => { ro.disconnect(); chartRef.current?.dispose(); chartRef.current = null; };
  }, [items]);

  return <div ref={ref} style={{ width: '100%', height: 260 }} />;
};

// --- ECharts Combo Trend (Bar + Line, dual axis) ---------------------------
const ComboTrendChart: React.FC<{ xTicks: string[]; bar: number[]; line: number[] }> = ({ xTicks, bar, line }) => {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<echarts.EChartsType | null>(null);

  React.useEffect(() => {
    if (!ref.current) return;
    chartRef.current?.dispose();
    chartRef.current = echarts.init(ref.current);

    const option: echarts.EChartsCoreOption = {
      grid: { left: 40, right: 40, top: 20, bottom: 40 },
      tooltip: { trigger: 'axis' },
      xAxis: [{ type: 'category', data: xTicks }],
      yAxis: [{ type: 'value' }, { type: 'value', position: 'right' }],
      series: [
        { name: t('dashboard.trend.bar'), type: 'bar', data: bar, itemStyle: { color: '#5B8FF9' }, barWidth: 20 },
        { name: t('dashboard.trend.line'), type: 'line', yAxisIndex: 1, data: line, itemStyle: { color: '#5AD8A6' } },
      ],
    };

    chartRef.current.setOption(option);
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(ref.current);
    return () => { ro.disconnect(); chartRef.current?.dispose(); chartRef.current = null; };
  }, [xTicks, bar, line, t]);

  return <div ref={ref} style={{ width: '100%', height: 260 }} />;
};

const PieChart: React.FC<{ items: { name: string; value: number }[] }> = ({ items }) => {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<echarts.EChartsType | null>(null);
  React.useEffect(() => {
    if (!ref.current) return;
    chartRef.current?.dispose();
    chartRef.current = echarts.init(ref.current);
    const option: echarts.EChartsCoreOption = {
      tooltip: { trigger: 'item' },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
          label: { show: true, formatter: '{b}: {d}%' },
          data: items,
        },
      ],
    };
    chartRef.current.setOption(option);
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(ref.current);
    return () => { ro.disconnect(); chartRef.current?.dispose(); chartRef.current = null; };
  }, [items, t]);
  return <div ref={ref} style={{ width: '100%', height: 260 }} />;
};

const StackedModeBarChart: React.FC<{
  ticks: string[];
  standard: number[];
  submission: number[];
  standardName: string;
  submissionName: string;
}> = ({ ticks, standard, submission, standardName, submissionName }) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<echarts.EChartsType | null>(null);
  React.useEffect(() => {
    if (!ref.current) return undefined;
    chartRef.current?.dispose();
    chartRef.current = echarts.init(ref.current);
    const option: echarts.EChartsCoreOption = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { bottom: 0, data: [standardName, submissionName] },
      grid: { left: 8, right: 16, top: 16, bottom: 32, containLabel: true },
      xAxis: { type: 'category', data: ticks, axisTick: { alignWithLabel: true } },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        {
          name: standardName,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#5B8FF9' },
          emphasis: { focus: 'series' },
          data: standard,
        },
        {
          name: submissionName,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#5AD8A6', borderRadius: [4, 4, 0, 0] },
          emphasis: { focus: 'series' },
          data: submission,
        },
      ],
    };
    chartRef.current.setOption(option);
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(ref.current);
    return () => { ro.disconnect(); chartRef.current?.dispose(); chartRef.current = null; };
  }, [ticks, standard, submission, standardName, submissionName]);
  return <div ref={ref} style={{ width: '100%', height: 260 }} />;
};

const ChartCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; color?: 'blue' | 'green' | 'teal'; onDownload?: () => void; downloadLabel?: string; extra?: React.ReactNode; loading?: boolean }> = ({ title, subtitle, children, onDownload, downloadLabel, extra, loading = false }) => {
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: 360, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 1, p: 1.25, borderRadius: 1, minHeight: 64, color: 'rgba(0, 0, 0, 0.87)', background: 'linear-gradient(158deg, #FFF7F6 40%, #E1E9FF 100%)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box>
            <Typography variant="subtitle2">{title}</Typography>
            {subtitle && (
              <Typography variant="subtitle2">{subtitle}</Typography>
            )}
          </Box>
          {extra}
        </Box>
        {onDownload && (
          <Button size="small" variant="text" endIcon={<DownloadIcon />} onClick={onDownload} disabled={loading}>
            {downloadLabel || t('dashboard.downloadData', { defaultValue: '下载数据' })}
          </Button>
        )}
      </Box>
      <Box sx={{ position: 'relative', flex: 1 }}>
        {children}
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)' }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// --- Metric Card ------------------------------------------------------------
const MetricCard: React.FC<{ metric: Metric; tone?: 'dynamic' | 'static'; icon?: React.ReactNode; loading?: boolean; tip?: string }> = ({ metric, tone = 'dynamic', icon, loading = false, tip }) => (
  <Paper
    elevation={0}
    sx={{
      p: 0,
      borderRadius: 2,
      border: '1px solid',
      borderColor: tone === 'dynamic' ? '#fad6c7' : '#bde3ff',
      background: tone === 'dynamic' ? '#fff7f5' : 'rgba(20, 130, 250, 0.05)',
      position: 'relative',
    }}
  >
    {tip && (
      <Box sx={{ position: 'absolute', top: 6, right: 6, color: tone === 'dynamic' ? '#cc0033' : '#1482fa' }}>
        <Tooltip title={tip}>
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
    )}
    {/* <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRight: '1px solid',
      borderColor: tone === 'dynamic' ? '#FFD6DC' : '#C7D2FF',
      backgroundColor: tone === 'dynamic' ? '#FFE3E8' : '#E6EDFF',
      p: 1.25,
      borderRadius: '8px'
    }}>
      <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tone === 'dynamic' ? '#E5566E' : '#5B8FF9' }}>
        {icon ?? (tone === 'dynamic' ? <TrendingUpIcon fontSize="small" /> : <LibraryBooksIcon fontSize="small" />)}
      </Box>
    </Box> */}
    <Box sx={{ p: 2 }}>
      <Box sx={{display: 'flex', justifyContent: 'center'}}>
        <Box sx={{ width: 28, height: 28, color: tone === 'dynamic' ? '#ff8782' : '#1482fa' }}>
          {icon ?? (tone === 'dynamic' ? <TrendingUpIcon fontSize="small" /> : <LibraryBooksIcon fontSize="small" />)}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">{metric.label}</Typography>
      <Box sx={{ mt: 1, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? <CircularProgress size={20} /> : <Typography variant="h5">{metric.value}</Typography>}
      </Box>
    </Box>
  </Paper>
);

// --- Dashboard Page ---------------------------------------------------------
export const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [range, setRange] = React.useState<RangeKey>('week');
  const [dateStr, setDateStr] = React.useState<string>('');
  const [fromStr, setFromStr] = React.useState<string>('');
  const [toStr, setToStr] = React.useState<string>('');
  const [topTab, setTopTab] = React.useState<'user' | 'dept'>('user');
  const [memberCharScope, setMemberCharScope] = React.useState<'total' | 'cn' | 'en'>('total');
  const isZh = (i18n.language || navigator.language).startsWith('zh');
  const dispatch = useDispatch<any>();

  const baseDate = React.useMemo(() => {
    const d = dateStr ? DateTime.fromISO(dateStr) : DateTime.local();
    return d.isValid ? d : DateTime.local();
  }, [dateStr]);

  const [overviewDynamicData, setOverviewDynamicData] = React.useState<Metric[]>([
    { label: t('dashboard.metrics.userCounts', { defaultValue: '翻译用户数' }), value: 0 },
    { label: t('dashboard.metrics.totalDocuments', { defaultValue: '翻译文档总数' }), value: 0 },
    { label: t('dashboard.metrics.cnVolume', { defaultValue: '中文翻译量' }), value: 0 },
    { label: t('dashboard.metrics.enVolume', { defaultValue: '英文翻译量' }), value: 0 },
  ]);
  const [loadingDynamic, setLoadingDynamic] = React.useState<boolean>(true);
  React.useEffect(() => {
    const today = DateTime.local();
    setToStr(today.toISODate() || '');
    setFromStr(today.minus({ days: 7 }).toISODate() || '');
  }, []);
  // Single source of truth for the date window. Presets set from/to directly; the
  // 'custom' preset keeps whatever the user typed in the From/To pickers. Every
  // section (overview cards + all history charts) reads the same from/to below.
  const applyPreset = React.useCallback((key: RangeKey) => {
    setRange(key);
    if (key === 'custom') return;
    const today = DateTime.local();
    setToStr(today.toISODate() || '');
    const from =
      key === 'yesterday' ? today.minus({ days: 1 })
      : key === 'week' ? today.minus({ days: 7 })
      : today.minus({ days: 30 });
    setFromStr(from.toISODate() || '');
  }, []);
  React.useEffect(() => {
    if (!fromStr || !toStr) return;
    const from = fromStr;
    const to = toStr;
    (async () => {
      setLoadingDynamic(true);
      try {
        const resp: any = await makeRequest({
          url: '/api/v1/dashboard/summary',
          method: 'POST',
          data: {
            payload: ['user_count', 'doc_count', 'zh_charts', 'en_charts'],
            from,
            to,
          },
        });
        const data = resp?.data ?? resp ?? {};
        const userCount = Number(data.user_count ?? 0);
        const docCount = Number(data.doc_count ?? 0);
        const zhChars = Number((data.zh_charts ?? data.zh_charts) ?? 0);
        const enChars = Number((data.en_charts ?? data.en_charts) ?? 0);
        setOverviewDynamicData([
          { label: t('dashboard.metrics.userCounts', { defaultValue: '翻译用户数' }), value: userCount },
          { label: t('dashboard.metrics.totalDocuments', { defaultValue: '翻译文档总数' }), value: docCount },
          { label: t('dashboard.metrics.cnVolume', { defaultValue: '中文翻译量' }), value: zhChars },
          { label: t('dashboard.metrics.enVolume', { defaultValue: '英文翻译量' }), value: enChars },
        ]);
      } catch {
        setOverviewDynamicData([
          { label: t('dashboard.metrics.userCounts', { defaultValue: '翻译用户数' }), value: 0 },
          { label: t('dashboard.metrics.totalDocuments', { defaultValue: '翻译文档总数' }), value: 0 },
          { label: t('dashboard.metrics.cnVolume', { defaultValue: '中文翻译量' }), value: 0 },
          { label: t('dashboard.metrics.enVolume', { defaultValue: '英文翻译量' }), value: 0 },
        ]);
      } finally {
        setLoadingDynamic(false);
      }
    })();
  }, [fromStr, toStr, t]);
  const [overviewStaticValues, setOverviewStaticValues] = React.useState<number[]>([0, 0, 0, 0]);
  const [loadingStatic, setLoadingStatic] = React.useState<boolean>(true);
  React.useEffect(() => {
    (async () => {
      setLoadingStatic(true);
      try {
        const resp: any = await makeRequest({
          url: '/api/v1/dashboard/summary',
          method: 'POST',
          data: {
            payload: ['glossaryLibraries', 'glossaryEntries', 'referenceLibraries', 'referenceEntries'],
          },
        });
        const data = resp?.data ?? resp ?? {};
        const glossaryLibraries = Number(data.glossaryLibraries ?? data.glossary_libraries ?? 0);
        const glossaryEntries = Number(data.glossaryEntries ?? data.glossary_entries ?? 0);
        const referenceLibraries = Number(data.referenceLibraries ?? data.reference_libraries ?? 0);
        const referenceEntries = Number(data.referenceEntries ?? data.reference_entries ?? 0);
        setOverviewStaticValues([glossaryLibraries, glossaryEntries, referenceLibraries, referenceEntries]);
      } catch {
        setOverviewStaticValues([0, 0, 0, 0]);
      } finally {
        setLoadingStatic(false);
      }
    })();
  }, [t]);
  const overviewStaticData = React.useMemo<Metric[]>(() => [
    { label: t('dashboard.metrics.termLibs', { defaultValue: '术语文件总数' }), value: overviewStaticValues[0] ?? 0 },
    { label: t('dashboard.metrics.termEntries', { defaultValue: '术语词条总数' }), value: overviewStaticValues[1] ?? 0 },
    { label: t('dashboard.metrics.caseLibs', { defaultValue: '样例文件总数' }), value: overviewStaticValues[2] ?? 0 },
    { label: t('dashboard.metrics.caseEntries', { defaultValue: '样例词条数' }), value: overviewStaticValues[3] ?? 0 },
  ], [overviewStaticValues, t]);

  const [docCountsTicks, setDocCountsTicks] = React.useState<string[]>([]);
  const [docCountsValues, setDocCountsValues] = React.useState<number[]>([]);
  const [docTypes, setDocTypes] = React.useState<{ name: string; value: number }[]>([]);
  const [activeUsersTicks, setActiveUsersTicks] = React.useState<string[]>([]);
  const [activeUsersValues, setActiveUsersValues] = React.useState<number[]>([]);
  const [memberDocsRanking, setMemberDocsRanking] = React.useState<RankItem[]>([]);
  const [teamDocsRanking, setTeamDocsRanking] = React.useState<RankItem[]>([]);
  const [externalMemberDocsRanking, setExternalMemberDocsRanking] = React.useState<RankItem[]>([]);
  const [memberCharsTotal, setMemberCharsTotal] = React.useState<RankItem[]>([]);
  const [memberCharsZh, setMemberCharsZh] = React.useState<RankItem[]>([]);
  const [memberCharsEn, setMemberCharsEn] = React.useState<RankItem[]>([]);
  const [successRateData, setSuccessRateData] = React.useState<{ name: string; value: number }[]>([]);
  // Success/failure split per translation mode, plus daily volume stacked by mode.
  const [successRateStandard, setSuccessRateStandard] = React.useState<{ name: string; value: number }[]>([]);
  const [successRateSubmission, setSuccessRateSubmission] = React.useState<{ name: string; value: number }[]>([]);
  const [modeTicks, setModeTicks] = React.useState<string[]>([]);
  const [modeStandardValues, setModeStandardValues] = React.useState<number[]>([]);
  const [modeSubmissionValues, setModeSubmissionValues] = React.useState<number[]>([]);
  const [loadingPanel3, setLoadingPanel3] = React.useState<boolean>(true);
  const [loadingSuccess, setLoadingSuccess] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (!fromStr || !toStr) return;
    (async () => {
      setLoadingPanel3(true);
      try {
        const resp: any = await makeRequest({
          url: '/api/v1/dashboard/panel3',
          method: 'POST',
          data: {
            payload: ['daily_doc_counts', 'doc_type_ratio', 'daily_active_users', 'member_doc_count_ranking', 'external_member_doc_count_ranking', 'team_doc_count_ranking', 'member_char_count_ranking', 'daily_doc_counts_by_mode'],
            from: fromStr,
            to: toStr,
          },
        });
        const data = resp?.data ?? resp ?? {};
        const dailyDocCounts = Array.isArray(data.daily_doc_counts) ? data.daily_doc_counts : [];
        setDocCountsTicks(dailyDocCounts.map((d: any) => String(d.date || '')));
        setDocCountsValues(dailyDocCounts.map((d: any) => Number(d.count ?? 0)));

        const typeRatio = Array.isArray(data.doc_type_ratio) ? data.doc_type_ratio : [];
        setDocTypes(typeRatio.map((it: any) => ({ name: String(it.type || 'Unknown'), value: Number(it.count ?? 0) })));

        const activeUsers = Array.isArray(data.daily_active_users) ? data.daily_active_users : [];
        setActiveUsersTicks(activeUsers.map((d: any) => String(d.date || '')));
        setActiveUsersValues(activeUsers.map((d: any) => Number(d.users ?? 0)));

        const memberRanking = Array.isArray(data.member_doc_count_ranking) ? data.member_doc_count_ranking : [];
        setMemberDocsRanking(memberRanking.map((it: any) => ({ name: String(it.username ?? it.userId ?? 'Unknown'), value: Number(it.count ?? it.value ?? 0) })));

        const teamRanking = Array.isArray(data.team_doc_count_ranking) ? data.team_doc_count_ranking : [];
        setTeamDocsRanking(teamRanking.map((it: any) => ({ name: String(it.team ?? 'Unknown'), value: Number(it.count ?? it.value ?? 0) })));

        const externalMemberRanking = Array.isArray(data.external_member_doc_count_ranking) ? data.external_member_doc_count_ranking : [];
        setExternalMemberDocsRanking(externalMemberRanking.map((it: any) => ({ name: String(it.username ?? it.email ?? 'Unknown'), value: Number(it.count ?? it.value ?? 0) })));

        const memberChars = data.member_char_count_ranking || {};
        const zhList = Array.isArray(memberChars.zh) ? memberChars.zh : [];
        const enList = Array.isArray(memberChars.en) ? memberChars.en : [];
        const totalList = Array.isArray(memberChars.total) ? memberChars.total : [];
        setMemberCharsZh(zhList.map((it: any) => ({ name: String(it.username ?? it.userId ?? 'Unknown'), value: Number(it.count ?? it.value ?? 0) })));
        setMemberCharsEn(enList.map((it: any) => ({ name: String(it.username ?? it.userId ?? 'Unknown'), value: Number(it.count ?? it.value ?? 0) })));
        setMemberCharsTotal(totalList.map((it: any) => ({ name: String(it.username ?? it.userId ?? 'Unknown'), value: Number(it.count ?? it.value ?? 0) })));

        const byMode = Array.isArray(data.daily_doc_counts_by_mode) ? data.daily_doc_counts_by_mode : [];
        setModeTicks(byMode.map((d: any) => String(d.date || '')));
        setModeStandardValues(byMode.map((d: any) => Number(d.standard ?? 0)));
        setModeSubmissionValues(byMode.map((d: any) => Number(d.submission ?? 0)));
      } catch (e) {
        setDocCountsTicks([]);
        setDocCountsValues([]);
        setDocTypes([]);
        setActiveUsersTicks([]);
        setActiveUsersValues([]);
        setMemberDocsRanking([]);
        setTeamDocsRanking([]);
        setExternalMemberDocsRanking([]);
        setMemberCharsZh([]);
        setMemberCharsEn([]);
        setMemberCharsTotal([]);
        setModeTicks([]);
        setModeStandardValues([]);
        setModeSubmissionValues([]);
      } finally {
        setLoadingPanel3(false);
      }
    })();
  }, [fromStr, toStr]);

  React.useEffect(() => {
    if (!fromStr || !toStr) return;
    (async () => {
      setLoadingSuccess(true);
      try {
        const succLabel = t('dashboard.success', { defaultValue: '成功' });
        const failLabel = t('dashboard.fail', { defaultValue: '失败' });
        const [resp, modeResp]: any[] = await Promise.all([
          makeRequest({
            url: '/api/v1/dashboard/summary',
            method: 'POST',
            data: {
              payload: ['succ_doc_count', 'fail_doc_count'],
              from: fromStr,
              to: toStr,
            },
          }),
          makeRequest({
            url: '/api/v1/dashboard/panel3',
            method: 'POST',
            data: {
              payload: ['success_rate_by_mode'],
              from: fromStr,
              to: toStr,
            },
          }),
        ]);
        const data = resp?.data ?? resp ?? {};
        const succ = Number(data.succ_doc_count ?? 0);
        const fail = Number(data.fail_doc_count ?? 0);
        setSuccessRateData([
          { name: succLabel, value: succ },
          { name: failLabel, value: fail },
        ]);

        const modeRows: any[] = (modeResp?.data ?? modeResp ?? {}).success_rate_by_mode ?? [];
        const pick = (mode: string) => {
          const row = modeRows.find((r) => r.mode === mode);
          return [
            { name: succLabel, value: Number(row?.succ ?? 0) },
            { name: failLabel, value: Number(row?.fail ?? 0) },
          ];
        };
        setSuccessRateStandard(pick('STANDARD'));
        setSuccessRateSubmission(pick('SUBMISSION'));
      } catch (e) {
        setSuccessRateData([]);
        setSuccessRateStandard([]);
        setSuccessRateSubmission([]);
      } finally {
        setLoadingSuccess(false);
      }
    })();
  }, [fromStr, toStr, t]);

  const historyData = React.useMemo(() => {
    const buildTicks = (): string[] => {
      const f = fromStr ? DateTime.fromISO(fromStr) : null;
      const tdate = toStr ? DateTime.fromISO(toStr) : null;
      if (f && f.isValid && tdate && tdate.isValid && tdate >= f) {
        const days = Math.floor(tdate.diff(f, 'days').days) + 1;
        const arr: string[] = [];
        for (let i = 0; i < days; i++) {
          arr.push(f.plus({ days: i }).toISODate() || '');
        }
        return arr;
      }
      const end = DateTime.local();
      const arr: string[] = [];
      for (let i = 6; i >= 0; i--) {
        arr.push(end.minus({ days: i }).toISODate() || '');
      }
      return arr;
    };
    const ticks = docCountsTicks.length ? docCountsTicks : buildTicks();
    const zeros = Array.from({ length: ticks.length }, () => 0);
    const xTicks = ticks;
    const valuesA = docCountsValues.length ? docCountsValues : zeros;
    const valuesB: number[] = [];
    const usageBar = activeUsersValues.length ? activeUsersValues : zeros;
    const usageLine = usageBar;
    const docTypesItems = docTypes.length ? docTypes : [];
    const memberByDocsItems = memberDocsRanking.length ? memberDocsRanking : [];
    const teamByDocsItems = teamDocsRanking.length ? teamDocsRanking : [];
    const externalMemberByDocsItems = externalMemberDocsRanking.length ? externalMemberDocsRanking : [];
    const successRateItems = successRateData.length ? successRateData : [
      { name: t('dashboard.success', { defaultValue: '成功' }), value: 0 },
      { name: t('dashboard.fail', { defaultValue: '失败' }), value: 0 },
    ];
    return {
      metrics: [],
      overviewDynamic: [],
      overviewStatic: [],
      xTicks,
      valuesA,
      valuesB,
      usageBar,
      usageLine,
      docTypes: docTypesItems,
      successRate: successRateItems,
      memberByChars: memberCharsTotal,
      memberByDocs: memberByDocsItems,
      teamByDocs: teamByDocsItems,
      externalMemberByDocs: externalMemberByDocsItems,
      topUsers: [],
      topDepartments: [],
      memberByCharsCn: memberCharsZh,
      memberByCharsEn: memberCharsEn,
    } as DashboardData;
  }, [fromStr, toStr, t, docCountsTicks, docCountsValues, docTypes, activeUsersValues, memberDocsRanking, teamDocsRanking, externalMemberDocsRanking, successRateData, memberCharsTotal, memberCharsZh, memberCharsEn]);

  const onExport = React.useCallback(() => {
    const lines: string[] = [];
    lines.push('Section,Label,Value');
    historyData.metrics.forEach((m) => lines.push(`Metrics,${m.label},${m.value}`));
    lines.push('');
    lines.push('Series,Tick,Series A,Series B');
    for (let i = 0; i < historyData.xTicks.length; i++) {
      const aVal = historyData.valuesA[i];
      const bVal = historyData.valuesB[i];
      lines.push(`Values,${historyData.xTicks[i]},${aVal},${bVal}`);
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_${range}_${baseDate.toFormat('yyyyLLdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [historyData, range, baseDate]);

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t('common.home')}</Link>
        <Typography color="textPrimary">{t('navigation.dashboard')}</Typography>
      </Breadcrumbs>
      <PaperCard>
        {/* <Typography variant="h5" sx={{ textAlign: 'left', mb: 2 }}>
          {t('navigation.dashboard')}
        </Typography> */}

        {/* Unified date filter: presets + custom range. Drives BOTH the overview
            cards and every history chart below (single source of truth: from/to). */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Typography variant="subtitle1" sx={{ textAlign: 'left' }}>{t('dashboard.overview', { defaultValue: '总览' })}</Typography>
          <Box sx={{ ml: 2 }}>
            <Tabs value={range} onChange={(_, v) => applyPreset(v as RangeKey)} aria-label="Range Tabs" sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36 } }}>
              <Tab label={t('dashboard.yesterday', { defaultValue: '昨天' })} value="yesterday" />
              <Tab label={t('dashboard.pastWeek', { defaultValue: '过去7天' })} value="week" />
              <Tab label={t('dashboard.pastMonth', { defaultValue: '过去30天' })} value="month" />
              <Tab label={t('dashboard.custom', { defaultValue: '自定义' })} value="custom" />
            </Tabs>
          </Box>
          {range === 'custom' && (
            <Stack direction="row" spacing={2} sx={{ ml: 2 }}>
              <TextField
                type="date"
                label={t('dashboard.timeFrom', { defaultValue: '时间 From' })}
                value={fromStr || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (toStr) {
                    const f = DateTime.fromISO(v);
                    const tdt = DateTime.fromISO(toStr);
                    if (f.isValid && tdt.isValid) {
                      const days = Math.floor(tdt.diff(f, 'days').days) + 1;
                      if (days < 2) {
                        dispatch(app.actions.setGlobalMessage({ message: '请选择至少两天的时间区间', status: 'error' }));
                        return;
                      }
                    }
                  }
                  setFromStr(v);
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: (() => {
                    const today = DateTime.local().toISODate() || undefined;
                    const limit = toStr ? DateTime.fromISO(toStr).minus({ days: 1 }).toISODate() || undefined : today;
                    if (!today || !limit) return today || limit;
                    return limit > today ? today : limit;
                  })(),
                }}
              />
              <TextField
                type="date"
                label={t('dashboard.timeTo', { defaultValue: '时间 To' })}
                value={toStr || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (fromStr) {
                    const f = DateTime.fromISO(fromStr);
                    const tdt = DateTime.fromISO(v);
                    if (f.isValid && tdt.isValid) {
                      const days = Math.floor(tdt.diff(f, 'days').days) + 1;
                      if (days < 2) {
                        dispatch(app.actions.setGlobalMessage({ message: '请选择至少两天的时间区间', status: 'error' }));
                        return;
                      }
                    }
                  }
                  setToStr(v);
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: DateTime.local().toISODate() || undefined,
                  min: fromStr ? DateTime.fromISO(fromStr).plus({ days: 1 }).toISODate() || undefined : undefined,
                }}
              />
            </Stack>
          )}
          <Box flex={1} />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {overviewDynamicData.map((m, idx) => (
            <Grid item xs={12} sm={6} md={3} key={`dyn-${idx}`}>
              <MetricCard
                metric={m}
                tone="dynamic"
                loading={loadingDynamic}
                tip={
                  idx === 2
                    ? t('dashboard.tips.cnChars', { defaultValue: '中文按照“字”统计' })
                    : idx === 3
                    ? t('dashboard.tips.enChars', { defaultValue: '英文按照“字母”统计' })
                    : undefined
                }
                icon={
                  idx === 0 ? <PeopleAltIcon /> :
                  idx === 1 ? <DescriptionIcon /> :
                  idx === 2 ? <GTranslateIcon /> :
                  <LanguageIcon />
                }
              />
            </Grid>
          ))}
        </Grid>

        <Typography variant="subtitle1" align="left" sx={{ mb: 1 }}>{t('dashboard.overviewStatic', { defaultValue: '术语和样例' })}</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {overviewStaticData.map((m, idx) => (
            <Grid item xs={12} sm={6} md={3} key={`st-${idx}`}>
              <MetricCard
                metric={m}
                tone="static"
                loading={loadingStatic}
                icon={
                  idx === 0 ? <BookOpenText /> :
                  idx === 1 ? <ViewWeekOutlinedIcon /> :
                  idx === 2 ? <LibraryBooksOutlinedIcon /> :
                  <WidgetsOutlinedIcon />
                }
              />
            </Grid>
          ))}
        </Grid>

        {/* History charts. The date window is controlled by the unified filter at the
            top of the page (no separate picker here anymore). */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ textAlign: 'left' }}>{t('dashboard.history', { defaultValue: '历史数据' })}</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
          <ChartCard
            title={t('dashboard.cards.totalDocs', { defaultValue: '文档总数' })}
            color="teal"
            loading={loadingPanel3}
            onDownload={async () => {
              const today = DateTime.local();
              const to = toStr || today.toISODate() || '';
              const from = fromStr || today.minus({ days: 7 }).toISODate() || '';
              const resp: any = await makeRequest({
                url: '/api/v1/dashboard/panel3/daily-doc-counts.csv',
                method: 'GET',
                responseType: 'blob',
                params: { from, to },
              });
              const blob = (resp && resp.data) instanceof Blob ? resp.data : new Blob([resp], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `daily_doc_counts_${from}_${to}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <LineChart data={historyData} single seriesName={t('dashboard.cards.totalDocs', { defaultValue: '文档总数' })} />
          </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.docTypes', { defaultValue: '文档类型' })}
              color="blue"
              loading={loadingPanel3}
              onDownload={async () => {
                const today = DateTime.local();
                const to = toStr || today.toISODate() || '';
                const from = fromStr || today.minus({ days: 7 }).toISODate() || '';
                const resp: any = await makeRequest({
                  url: '/api/v1/dashboard/panel3/doc-type-ratio.csv',
                  method: 'GET',
                  responseType: 'blob',
                  params: { from, to },
                });
                const blob = (resp && resp.data) instanceof Blob ? resp.data : new Blob([resp], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `doc_types_${from}_${to}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <PieChart items={historyData.docTypes} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.translateUsers', { defaultValue: '翻译用户' })}
              color="green"
              loading={loadingPanel3}
              onDownload={async () => {
                const today = DateTime.local();
                const to = toStr || today.toISODate() || '';
                const from = fromStr || today.minus({ days: 7 }).toISODate() || '';
                const resp: any = await makeRequest({
                  url: '/api/v1/dashboard/panel3/daily-active-users.csv',
                  method: 'GET',
                  responseType: 'blob',
                  params: { from, to },
                });
                const blob = (resp && resp.data) instanceof Blob ? resp.data : new Blob([resp], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `translate_users_${from}_${to}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <LineChart
                data={{
                  ...historyData,
                  xTicks: activeUsersTicks.length ? activeUsersTicks : historyData.xTicks,
                  valuesA: activeUsersValues.length ? activeUsersValues : historyData.usageBar,
                  valuesB: [],
                } as any}
                single
                seriesName={t('dashboard.cards.translateUsers', { defaultValue: '翻译用户' })}
              />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.member', { defaultValue: '翻译用户排名' })}
              subtitle={t('dashboard.cards.byChars', { defaultValue: '（按字符）' })}
              color="teal"
              loading={loadingPanel3}
              onDownload={async () => {
                const today = DateTime.local();
                const to = toStr || today.toISODate() || '';
                const from = fromStr || today.minus({ days: 7 }).toISODate() || '';
                const items =
                  memberCharScope === 'total'
                    ? historyData.memberByChars
                    : memberCharScope === 'cn'
                    ? historyData.memberByCharsCn
                    : historyData.memberByCharsEn;
                const limit = (items && Array.isArray(items)) ? items.length : 5;
                const type =
                  memberCharScope === 'en'
                    ? 'en_us'
                    : memberCharScope === 'cn'
                    ? 'zh_cn'
                    : 'all';
                const resp: any = await makeRequest({
                  url: '/api/v1/dashboard/panel3/member-char-count-ranking.csv',
                  method: 'GET',
                  responseType: 'blob',
                  params: { limit, from, to, type },
                });
                const blob = (resp && resp.data) instanceof Blob ? resp.data : new Blob([resp], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `member_by_chars_${from}_${to}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              extra={
                <TextField
                  select
                  size="small"
                  value={memberCharScope}
                  onChange={(e) => setMemberCharScope(e.target.value as 'total' | 'cn' | 'en')}
                  sx={{ minWidth: 200, '& .MuiSelect-select, & .MuiInputBase-input': { textAlign: 'left' } }}
                >
                  <MenuItem value="total">{t('dashboard.charSelect.total', { defaultValue: '总量（字符数）' })}</MenuItem>
                  <MenuItem value="cn">{t('dashboard.charSelect.cn', { defaultValue: '中文（字符数）' })}</MenuItem>
                  <MenuItem value="en">{t('dashboard.charSelect.en', { defaultValue: '英文（字母）' })}</MenuItem>
                </TextField>
              }
            >
              <RankingChart
                items={
                  memberCharScope === 'total'
                    ? historyData.memberByChars
                    : memberCharScope === 'cn'
                    ? historyData.memberByCharsCn
                    : historyData.memberByCharsEn
                }
              />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.successRateStandard', { defaultValue: '翻译文档记录成功率（标准模式）' })}
              color="blue"
              loading={loadingSuccess}
            >
              <PieChart items={successRateStandard} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.successRateSubmission', { defaultValue: '翻译文档记录成功率（递交模式）' })}
              color="blue"
              loading={loadingSuccess}
            >
              <PieChart items={successRateSubmission} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.docCountsByMode', { defaultValue: '每日文档数（按模式）' })}
              color="blue"
              loading={loadingPanel3}
            >
              <StackedModeBarChart
                ticks={modeTicks}
                standard={modeStandardValues}
                submission={modeSubmissionValues}
                standardName={t('dashboard.mode.standard', { defaultValue: '标准模式' })}
                submissionName={t('dashboard.mode.submission', { defaultValue: '递交模式' })}
              />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.member', { defaultValue: '翻译用户排名' })}
              subtitle={t('dashboard.cards.byDocs', { defaultValue: '（按文档）' })}
              color="green"
              loading={loadingPanel3}
              onDownload={async () => {
                const today = DateTime.local();
                const to = toStr || today.toISODate() || '';
                const from = fromStr || today.minus({ days: 7 }).toISODate() || '';
                const limit = (historyData && Array.isArray(historyData.memberByDocs)) ? historyData.memberByDocs.length : 5;
                const resp: any = await makeRequest({
                  url: '/api/v1/dashboard/panel3/member-doc-count-ranking.csv',
                  method: 'GET',
                  responseType: 'blob',
                  params: { limit, from, to },
                });
                const blob = (resp && resp.data) instanceof Blob ? resp.data : new Blob([resp], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `member_by_docs_${from}_${to}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <RankingChart items={historyData.memberByDocs} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.team', { defaultValue: '部门排名' })}
              subtitle={t('dashboard.cards.teamSub', { defaultValue: '（STANDARD 外部接口 · 按文档）' })}
              color="teal"
              loading={loadingPanel3}
              onDownload={async () => {
                const today = DateTime.local();
                const to = toStr || today.toISODate() || '';
                const from = fromStr || today.minus({ days: 7 }).toISODate() || '';
                const limit = (historyData && Array.isArray(historyData.teamByDocs)) ? historyData.teamByDocs.length : 5;
                const resp: any = await makeRequest({
                  url: '/api/v1/dashboard/panel3/team-doc-count-ranking.csv',
                  method: 'GET',
                  responseType: 'blob',
                  params: { limit, from, to },
                });
                const blob = (resp && resp.data) instanceof Blob ? resp.data : new Blob([resp], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `team_by_docs_${from}_${to}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <RankingChart items={historyData.teamByDocs} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard
              title={t('dashboard.cards.externalMember', { defaultValue: '外部接口用户排名' })}
              subtitle={t('dashboard.cards.externalMemberSub', { defaultValue: '（SUBMISSION 外部接口 · 按文档）' })}
              color="teal"
              loading={loadingPanel3}
              onDownload={async () => {
                const today = DateTime.local();
                const to = toStr || today.toISODate() || '';
                const from = fromStr || today.minus({ days: 7 }).toISODate() || '';
                const limit = (historyData && Array.isArray(historyData.externalMemberByDocs)) ? historyData.externalMemberByDocs.length : 5;
                const resp: any = await makeRequest({
                  url: '/api/v1/dashboard/panel3/external-member-doc-count-ranking.csv',
                  method: 'GET',
                  responseType: 'blob',
                  params: { limit, from, to },
                });
                const blob = (resp && resp.data) instanceof Blob ? resp.data : new Blob([resp], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `external_member_by_docs_${from}_${to}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <RankingChart items={historyData.externalMemberByDocs} />
            </ChartCard>
          </Grid>
        </Grid>
      </PaperCard>
    </Container>
  );
};

export default Dashboard;
